import CardAvatar from "@/components/cardElements/CardAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ActionItem, ContextActions } from "@/components/molecules/context-actions/ContextActions";
import { getMessageBubbleRadius } from "../../../utils/getMessageBubbleRadius";
import { CornerUpLeft, TriangleAlert } from "lucide-react";
import { useState } from "react";
import ImageLightbox from "@/components/modules/realEstateDetail/RealEstateDetail/components/ImagesFeature/image-lightbox/ImageLightbox";
type CallToActionItem = {
  label: string;
  value: string;
  variant?: string;
};
type MessageImage = {
  id: string;
  url: string;
  width?: number | null;
  height?: number | null;
};
type Props = {
  role: "user" | "non-user";
  userImg?: string;
  userName?: string;
  content: string;
  badge?: string;
  isLastInBlock?: boolean;
  isFirstInBlock?: boolean;
  onClick?: () => void;
  callToActions?: CallToActionItem[];
  onCtaClick?: (value: string) => void;
  ctaDisabled?: boolean;
  deleted?: boolean;
  actions?: ActionItem[];
  replyQuote?: {
    author?: string;
    content: string;
    image?: string | null;
  };
  onQuoteClick?: () => void;
  attachments?: MessageImage[];
  status?: "sending" | "failed";
};

/** Indeterminate spinner shown over an image while it's being sent. */
function UploadOverlay() {
  const r = 16;
  const c = 2 * Math.PI * r;
  return <span className="absolute inset-0 flex items-center justify-center bg-black/35" data-sentry-component="UploadOverlay" data-sentry-source-file="ChatMessage.tsx">
      {/* Spin a wrapping element — CSS rotation on an <svg> uses the wrong
          transform-origin (corner) in most browsers. */}
      <span className="flex animate-spin">
        <svg width="44" height="44" viewBox="0 0 44 44" data-sentry-element="svg" data-sentry-source-file="ChatMessage.tsx">
          <circle cx="22" cy="22" r={r} fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="3" data-sentry-element="circle" data-sentry-source-file="ChatMessage.tsx" />
          <circle cx="22" cy="22" r={r} fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * 0.7} transform="rotate(-90 22 22)" data-sentry-element="circle" data-sentry-source-file="ChatMessage.tsx" />
        </svg>
      </span>
    </span>;
}
export function ChatMessage({
  role,
  content,
  userImg,
  userName,
  badge,
  isLastInBlock,
  isFirstInBlock,
  onClick,
  callToActions,
  onCtaClick,
  ctaDisabled,
  deleted,
  actions,
  replyQuote,
  onQuoteClick,
  attachments,
  status
}: Props) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const bubbleRadius = getMessageBubbleRadius(isFirstInBlock ?? false, isLastInBlock ?? false, role);
  const images = !deleted && attachments?.length ? attachments : [];
  const hasCaption = !!content && content.length > 0;
  // When an image and a caption stack as one message, the join between them is
  // flat on the tail side (image = "first" of the pair, caption = "last"),
  // while the outer corners still follow the conversation grouping.
  const imageRadius = hasCaption ? getMessageBubbleRadius(isFirstInBlock ?? false, false, role) : bubbleRadius;
  const captionRadius = images.length > 0 ? getMessageBubbleRadius(false, isLastInBlock ?? false, role) : bubbleRadius;
  return <>
    <div onClick={onClick} className={cn("flex", isFirstInBlock ? "mt-4" : "mt-0.5", role === "user" ? "justify-end" : "justify-start", isLastInBlock && "mb-4")}>
      <div className="flex items-end gap-2">
        {role === "non-user" && isLastInBlock && <CardAvatar className="w-7 h-7" imgUrl={userImg} name={userName} />}
        {role === "non-user" && !isLastInBlock && <div className="w-7" />}
        {(() => {
          const hasActions = !deleted && !!actions?.length;

          // Images float as their own card(s), outside the coloured bubble.
          // Single image keeps its natural aspect and the bubble corner rule.
          // Multiple images tile inside ONE rounded container (bubble rule on
          // the outer corners), inner corners square, thin gaps between them.
          const imagesNode = images.length === 1 ? (() => {
            const single = images[0];
            const hasDim = !!(single.width && single.height);
            return <button type="button" onClick={e => {
              e.stopPropagation();
              setLightboxIndex(0);
            }}
            // Reserve the box from the stored dimensions so the height
            // is known before the image downloads (no layout shift).
            style={hasDim ? {
              aspectRatio: `${single.width}/${single.height}`
            } : undefined} className={cn("relative block max-h-[300px] w-[240px] max-w-full overflow-hidden border border-border bg-muted", imageRadius)}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={single.url} alt="" className={cn("block w-full cursor-pointer object-cover", hasDim ? "h-full" : "max-h-[300px]")} />
                    {status === "sending" && <UploadOverlay />}
                  </button>;
          })() : images.length > 1 ? <div className={cn("relative grid w-[240px] max-w-full gap-0.5 overflow-hidden border border-border", imageRadius, images.length === 2 ? "aspect-[2/1] grid-cols-2" : "aspect-square grid-cols-2 grid-rows-2")}>
                {images.map((img, i) => <button key={img.id} type="button" onClick={e => {
              e.stopPropagation();
              setLightboxIndex(i);
            }} className={cn("relative overflow-hidden bg-muted",
            // 3 images: the first fills a tall column and the odd
            // one stacks on the side nearest the message tail.
            images.length === 3 && i === 0 && "row-span-2", images.length === 3 && i === 0 && role !== "user" && "col-start-2")}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={img.url} alt="" className="block h-full w-full cursor-pointer object-cover" />
                  </button>)}
                {status === "sending" && <UploadOverlay />}
              </div> : null;

          // The bubble holds only the non-image content (caption / badge / CTAs).
          const showBubble = deleted || hasCaption || !deleted && (!!badge || !!callToActions?.length);
          const bubble = showBubble ? <div className={cn("flex flex-col overflow-hidden p-1", captionRadius, deleted ? "bg-muted/60 text-muted-foreground" : role === "non-user" ? "bg-muted text-foreground" : "bg-primary text-white", !deleted && onClick && "cursor-pointer hover:bg-muted-strong", !deleted && hasActions && "cursor-pointer")}>
              {(hasCaption || deleted) && <p className={cn("p-1.5", deleted && "italic")}>{content}</p>}

              {!deleted && badge && <Badge className="m-1.5 border-white/60 border-2 rounded-full font-normal w-fit">
                  {badge}
                </Badge>}

              {!deleted && callToActions && callToActions.length > 0 && <div className="flex flex-wrap gap-2 m-1.5">
                  {callToActions.map(cta => <Button key={cta.value} variant={cta.variant as any ?? "outline"} size="xs" disabled={ctaDisabled} onClick={e => {
                e.stopPropagation();
                onCtaClick?.(cta.value);
              }}>
                      {cta.label}
                    </Button>)}
                </div>}
            </div> : null;
          const body = imagesNode ? <div className={cn("flex flex-col", role === "user" ? "items-end" : "items-start")}>
              {imagesNode}
              {/* Caption tucks a bit under the image so they read as one
                  message — same overlap trick as the reply quote. */}
              {bubble && <div className="relative z-10 -mt-2">{bubble}</div>}
            </div> : bubble;
          const bodyWithActions = hasActions ? <ContextActions actions={actions!} menuModal bareLift>
              {body}
            </ContextActions> : body;

          // Sending shows an upload overlay on the image; a failed send shows a
          // red alert under the message. (Text-only sends have no pending state.)
          const bodyWithStatus = status === "failed" ? <div className={cn("flex flex-col", role === "user" ? "items-end" : "items-start")}>
                {bodyWithActions}
                <span className="mt-0.5 px-1 text-destructive">
                  <TriangleAlert className="h-3.5 w-3.5" />
                </span>
              </div> : bodyWithActions;
          if (deleted || !replyQuote) return bodyWithStatus;
          return <div className={cn("flex flex-col", role === "user" ? "items-end" : "items-start")}>
              <span className={cn("flex items-center gap-1 px-2 text-[11px] text-faint-foreground", role === "user" ? "flex-row-reverse" : "flex-row")}>
                <CornerUpLeft className="h-3 w-3" />
                {replyQuote.author}
              </span>
              <div onClick={onQuoteClick ? e => {
              e.stopPropagation();
              onQuoteClick();
            } : undefined} className={cn("-mb-2 flex max-w-[220px] items-center gap-2 rounded-lg bg-muted pb-4 text-xs text-foreground/60", replyQuote.image ? "pl-1 pr-3 pt-1" : "px-3 pt-1.5", onQuoteClick && "cursor-pointer hover:opacity-80")}>
                {replyQuote.image && <span className="relative h-8 w-8 shrink-0 overflow-hidden rounded-md">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={replyQuote.image} alt="" className="h-full w-full object-cover" />
                  </span>}
                <span className="truncate">{replyQuote.content}</span>
              </div>
              <div className="relative">{bodyWithStatus}</div>
            </div>;
        })()}
      </div>
    </div>
    {images.length > 0 && <ImageLightbox pictures={images.map(img => img.url)} open={lightboxIndex !== null} initialIndex={lightboxIndex ?? 0} onClose={() => setLightboxIndex(null)} manageHistory={false} />}
    </>;
}